import http from 'services/http';

export type MistakeSkill = 'listening' | 'reading' | 'sat_rw' | 'sat_math';

/** How a mistake is answered again; it never carries the right answer. */
export type RetryForm =
  | { kind: 'choice' | 'multi'; options: Array<{ id: string; text: string }> }
  | { kind: 'text' }
  | { kind: 'blanks'; blanks: string[] };

export type RetryAnswer = string | string[] | Record<string, string>;

export interface Mistake {
  key: string;
  attemptId: string;
  testId: string;
  testTitle: string;
  skill: MistakeSkill | null;
  /** Question HTML; render it with RenderedHtml, which sanitizes. */
  prompt: string;
  passageTitle: string | null;
  yourAnswer: string;
  /** Null when the question can only be revealed (matching, reordering). */
  retry: RetryForm | null;
  /** Already revealed: fixing it now earns no points. */
  revealed: boolean;
  /** Only sent once revealed. */
  correctAnswer: string | null;
  explanation: string | null;
  explanationLocked: boolean;
  answeredAt: string | null;
}

export interface MistakeList {
  items: Mistake[];
  mastered: number;
  fixed: number;
  points: number;
}

export interface MistakeAnswer {
  correctAnswer: string | null;
  explanation: string | null;
  explanationLocked: boolean;
}

export interface RetryResult extends MistakeAnswer {
  correct: boolean;
  pointsAwarded: number;
}

const path = (key: string) => `/api/portal/mistakes/${encodeURIComponent(key)}`;

export async function getMistakes(signal?: AbortSignal): Promise<MistakeList> {
  const { data } = await http.request.get<{ data: Partial<MistakeList> }>('/api/portal/mistakes', { signal });
  const list = data?.data;
  return {
    items: Array.isArray(list?.items) ? list.items : [],
    mastered: list?.mastered ?? 0,
    fixed: list?.fixed ?? 0,
    points: list?.points ?? 0,
  };
}

export async function retryMistake(key: string, answer: RetryAnswer): Promise<RetryResult> {
  const { data } = await http.request.post<{ data: RetryResult }>(`${path(key)}/retry`, { answer });
  return data.data;
}

export async function revealMistake(key: string): Promise<MistakeAnswer> {
  const { data } = await http.request.post<{ data: MistakeAnswer }>(`${path(key)}/reveal`);
  return data.data;
}

export async function setMistakeLearned(key: string, learned: boolean): Promise<void> {
  const url = `${path(key)}/learned`;
  if (learned) await http.request.put(url);
  else await http.request.delete(url);
}
